import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/api";
import { useSocketStore } from "@/store/useSocketStore";

type VisibilityFiltersPayload = {
  brandId: string;
  days?: "1" | "7" | "14" | "30" | "90";
  providers?: string;
  promptId?: string;
  includeActionables?: boolean;
  includeRunResponses?: boolean;
};

const buildFiltersSignature = (filters: VisibilityFiltersPayload) =>
  `${filters.brandId}:${filters.days ?? "7"}:${filters.providers ?? ""}:${
    filters.promptId ?? ""
  }:${filters.includeActionables ? "1" : "0"}:${
    filters.includeRunResponses === false ? "0" : "1"
  }`;

export const useVisibilitySocketBridge = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const { ensureSocket, getVisibilityQueryKey } = useSocketStore();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const socket = ensureSocket(API_BASE_URL);
    const handleResults = (payload: { brandId?: string; filters?: VisibilityFiltersPayload } & Record<string, any>) => {
      if (!payload?.filters?.brandId) return;
      const signature = buildFiltersSignature(payload.filters);
      const queryKey = getVisibilityQueryKey(signature);
      if (!queryKey) return;
      const { filters: _filters, brandId: _brandId, ...data } = payload;
      const isInvalidationOnly =
        Object.keys(data).length === 1 && data.updatedAt !== undefined;
      if (isInvalidationOnly) {
        void queryClient.invalidateQueries({
          queryKey,
          refetchType: "active",
        });
        return;
      }
      queryClient.setQueryData(queryKey, data);
    };
    socket.on("visibility:results", handleResults);
    return () => {
      socket.off("visibility:results", handleResults);
    };
  }, [enabled, ensureSocket, getVisibilityQueryKey, queryClient]);
};
